import React, { useState, useEffect } from 'react';
import { LabValue } from '../types';
import { ExtractionIssue } from '../services/extractionValidationService';

interface ValueCorrectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  marker: string;
  labValue: LabValue;
  issues: ExtractionIssue[];
  onSave: (marker: string, correctedValue: number, correctedUnit?: string) => void;
}

export const ValueCorrectionModal: React.FC<ValueCorrectionModalProps> = ({
  isOpen,
  onClose,
  marker,
  labValue,
  issues,
  onSave
}) => {
  const [correctedValue, setCorrectedValue] = useState(labValue.value.toString());
  const [correctedUnit, setCorrectedUnit] = useState(labValue.units);
  const [activeTab, setActiveTab] = useState<'quick' | 'manual'>('quick');
  
  // Find the main issue and suggested fix
  const mainIssue = issues.find(i => i.severity === 'error') || issues[0];
  const suggestedUnit = mainIssue?.suggestedFix?.units;
  
  useEffect(() => {
    // Reset when modal opens
    if (isOpen) {
      setCorrectedValue(labValue.value.toString());
      setCorrectedUnit(labValue.units);
      setActiveTab('quick');
    }
  }, [isOpen, labValue]);
  
  const handleQuickFix = () => {
    if (suggestedUnit) {
      onSave(marker, parseFloat(correctedValue), suggestedUnit);
    }
  };
  
  const handleManualSave = () => {
    const value = parseFloat(correctedValue);
    if (!isNaN(value)) {
      onSave(marker, value, correctedUnit);
    }
  };
  
  const getCommonUnits = () => {
    const markerLower = marker.toLowerCase();
    if (markerLower.includes('hdl') || markerLower.includes('ldl') || markerLower.includes('cholesterol')) {
      return ['mmol/L', 'mg/dL'];
    }
    if (markerLower.includes('glucose')) {
      return ['mmol/L', 'mg/dL'];
    }
    if (markerLower.includes('triglyceride')) {
      return ['mmol/L', 'mg/dL'];
    }
    if (markerLower.includes('hemoglobin') || markerLower === 'hb') {
      return ['g/dL', 'g/L'];
    }
    return [];
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Fix {marker} Value
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Current: {labValue.value} {labValue.units}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Error Summary */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="text-sm">
              <p className="font-medium text-red-900">{mainIssue.message}</p>
              {mainIssue.details && (
                <p className="text-red-700 mt-1">{mainIssue.details}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('quick')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              activeTab === 'quick' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Quick Fix
          </button>
          <button
            onClick={() => setActiveTab('manual')}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
              activeTab === 'manual' 
                ? 'bg-blue-100 text-blue-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Manual Entry
          </button>
        </div>
        
        {/* Content */}
        {activeTab === 'quick' && suggestedUnit ? (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-3">Suggested Correction</h3>
              
              <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Change unit from</p>
                  <p className="font-mono font-semibold text-lg">
                    {labValue.units} → {suggestedUnit}
                  </p>
                </div>
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <p className="text-xs text-blue-700 mt-3">
                This will keep the numeric value ({labValue.value}) but correct the unit
              </p>
            </div>
            
            <button
              onClick={handleQuickFix}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Apply Suggested Fix
            </button>
          </div>
        ) : activeTab === 'quick' ? (
          <div className="text-center py-8 text-gray-500">
            <p>No automatic fix available</p>
            <button
              onClick={() => setActiveTab('manual')}
              className="text-blue-600 hover:text-blue-800 font-medium mt-2"
            >
              Use manual entry instead
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value
              </label>
              <input
                type="number"
                step="any"
                value={correctedValue}
                onChange={(e) => setCorrectedValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter correct value"
              />
            </div>
            
            {getCommonUnits().length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <div className="flex gap-2">
                  {getCommonUnits().map(unit => (
                    <button
                      key={unit}
                      onClick={() => setCorrectedUnit(unit)}
                      className={`flex-1 py-2 px-3 rounded-lg border transition-colors ${
                        correctedUnit === unit
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {unit}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <button
              onClick={handleManualSave}
              disabled={!correctedValue || isNaN(parseFloat(correctedValue))}
              className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save Changes
            </button>
          </div>
        )}
        
        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-xs text-gray-500 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Always verify values against your original PDF before saving
          </p>
        </div>
      </div>
    </div>
  );
};